import { Link, useLocation, useRouterState } from "@tanstack/react-router"
import { EntireLogo } from "@ui/components/EntireLogo"
import { Button } from "@ui/components/primitives/Button"
import { usePostHog } from "posthog-js/react"
import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from "react"

import { isSiteNavGroup, isActivePath, siteNav } from "@/components/nav-data"
import { CloseIcon } from "@/components/primitives/icons/CloseIcon"
import { MenuIcon } from "@/components/primitives/icons/MenuIcon"
import { warmDashboardIntent, warmSignInIntent } from "@/lib/navigationWarmup"
import { HOME_HERO_BOUNDARY_ID } from "@/routes/-components/home/homeHeroBoundary"

import { SafariTopBarTint } from "./SafariTopBarTint"
import { getEffectiveHeaderVariant, type SiteHeaderVariant } from "./siteHeaderVariant"
import { SiteMobileMenu } from "./SiteMobileMenu"
import { SiteNavMenu } from "./SiteNavMenu"
import { SiteSection } from "./SiteSection"

const headerRailClassName =
  "flex h-16 items-center justify-between px-4 md:grid md:grid-cols-[1fr_auto_1fr]"
const navItemClassName =
  "text-site-body-sm flex h-8 items-center rounded-lg px-2 py-1 font-sans font-normal tracking-[-0.01em] transition-colors focus:outline-2 focus:-outline-offset-2 focus:outline-transparent focus:transition-none focus-visible:outline-focus"
const pillShadowClassName =
  "shadow-[0_0_0_1px_rgb(0_0_0/0.06),0_1px_2px_-1px_rgb(0_0_0/0.06),0_2px_4px_rgb(0_0_0/0.04)] dark:shadow-[0_0_0_1px_rgb(255_255_255/0.08),0_1px_2px_-1px_rgb(0_0_0/0.4),0_2px_4px_rgb(0_0_0/0.3)]"
const whiteControlClassName =
  "border-transparent! bg-white! text-[#333]! [text-shadow:none] hover:bg-neutral-100! active:bg-neutral-200!"
// Keep this aligned with the mobile h-16 rail in headerRailClassName.
const MOBILE_HEADER_RAIL_HEIGHT_PX = 64

const headerVariantStyles = {
  standard: {
    railClassName: "bg-(--surface-base) text-default",
    navItemClassName:
      "text-default hover:bg-(--surface-hover) active:bg-(--surface-active) data-[state=open]:bg-(--surface-hover) data-[state=open]:text-default",
    activeNavItemClassName: "text-default",
    ctaVariant: "attention",
    ctaClassName: `${pillShadowClassName} bg-stone-900! hover:bg-stone-800! active:bg-stone-700! dark:bg-neutral-100! dark:text-neutral-900! dark:hover:bg-neutral-300! dark:active:bg-neutral-400!`,
    mobileMenuButtonClassName: whiteControlClassName,
  },
  hero: {
    railClassName:
      "bg-transparent text-white [text-shadow:0_1px_2px_rgb(0_0_0/0.35)] [&_:focus-visible]:outline-white/90",
    navItemClassName:
      "text-white/80 hover:bg-white/10 hover:text-white data-[state=open]:bg-white/10 data-[state=open]:text-white",
    activeNavItemClassName: "text-white",
    ctaVariant: "ghost",
    ctaClassName: `${pillShadowClassName} ${whiteControlClassName}`,
    mobileMenuButtonClassName: whiteControlClassName,
  },
} as const satisfies Record<
  SiteHeaderVariant,
  {
    railClassName: string
    navItemClassName: string
    activeNavItemClassName: string
    ctaVariant: "attention" | "ghost"
    ctaClassName: string
    mobileMenuButtonClassName: string
  }
>

function getNavLinkClassName(active: boolean, variant: SiteHeaderVariant) {
  const styles = headerVariantStyles[variant]
  return `${navItemClassName} ${styles.navItemClassName} ${active ? styles.activeNavItemClassName : ""}`
}

const focusableSelector = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",")

function getFocusableElements(container: HTMLElement) {
  return Array.from(container.querySelectorAll<HTMLElement>(focusableSelector)).filter(
    (element) => !element.hasAttribute("disabled") && element.offsetParent !== null,
  )
}

function setElementInert(element: Element, inert: boolean) {
  const htmlElement = element as HTMLElement & { inert: boolean }
  htmlElement.inert = inert
}

function warmupHandlers(onWarmup: () => void) {
  return {
    onFocus: onWarmup,
    onMouseDown: onWarmup,
    onMouseEnter: onWarmup,
    onTouchStart: onWarmup,
  }
}

interface SiteHeaderProps {
  variant?: SiteHeaderVariant
}

interface HeaderShellProps {
  children: ReactNode
}

function HeroHeaderShell({ children }: HeaderShellProps) {
  return (
    <>
      <SafariTopBarTint
        boundaryId={HOME_HERO_BOUNDARY_ID}
        handoffOffsetPx={MOBILE_HEADER_RAIL_HEIGHT_PX}
      />
      <SiteSection
        fullWidth
        borderBottom={false}
        innerClassName={`${headerRailClassName} mx-auto max-w-320 min-[1312px]:px-0 ${headerVariantStyles.hero.railClassName}`}
        outerClassName="relative z-20"
      >
        {children}
      </SiteSection>
    </>
  )
}

function StandardHeaderShell({ children, mobileOpen }: HeaderShellProps & { mobileOpen: boolean }) {
  return (
    <SiteSection
      fullWidth
      innerClassName={`${headerRailClassName} mx-auto max-w-320 min-[1312px]:px-0 ${headerVariantStyles.standard.railClassName}`}
      outerClassName={`relative z-20 ${mobileOpen ? "sticky top-0 z-50" : ""}`}
    >
      {children}
    </SiteSection>
  )
}

interface HeaderContentProps {
  loginHref: string
  menuButtonRef: RefObject<HTMLButtonElement | null>
  mobileOpen: boolean
  onToggleMobile: () => void
  pathname: string
  variant: SiteHeaderVariant
}

function HeaderContent({
  loginHref,
  menuButtonRef,
  mobileOpen,
  onToggleMobile,
  pathname,
  variant,
}: HeaderContentProps) {
  const posthog = usePostHog()
  const styles = headerVariantStyles[variant]
  const ctaVariant = mobileOpen ? "ghost" : styles.ctaVariant
  const ctaClassName = `${styles.ctaClassName} ${mobileOpen ? whiteControlClassName : ""}`

  return (
    <>
      <div className="flex items-center justify-self-start">
        <Link
          to="/about"
          aria-label="Entire home"
          className="hidden w-fit shrink-0 rounded-sm hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus [html[data-has-session=true]_&]:inline-flex"
        >
          <EntireLogo className="h-6 w-auto" />
        </Link>
        <Link
          to="/"
          aria-label="Entire home"
          className="hidden w-fit shrink-0 rounded-sm hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus [html[data-has-session=false]_&]:inline-flex"
        >
          <EntireLogo className="h-6 w-auto" />
        </Link>
      </div>
      <nav className="hidden items-center gap-3 md:flex">
        {siteNav.map((item) =>
          isSiteNavGroup(item) ? (
            <SiteNavMenu
              key={item.label}
              label={item.label}
              items={item.items}
              menuLabel={item.menuLabel}
              promo={item.promo}
              secondaryItems={item.secondaryItems}
              variant={variant}
            />
          ) : item.external ? (
            <a
              key={item.href}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer"
              className={getNavLinkClassName(false, variant)}
            >
              {item.label}
            </a>
          ) : (
            <Link
              key={item.href}
              to={item.href}
              className={getNavLinkClassName(isActivePath(pathname, item.href), variant)}
            >
              {item.label}
            </Link>
          ),
        )}
      </nav>
      <div className="flex items-center justify-end gap-2">
        {pathname !== "/login" && (
          <>
            <Button
              href="/home"
              variant={ctaVariant}
              size="small"
              className={`${ctaClassName} hidden [html[data-has-session=true]_&]:flex`}
              {...warmupHandlers(warmDashboardIntent)}
              onClick={() => posthog.capture("dashboard_clicked", { source: pathname })}
            >
              Dashboard
            </Button>
            <Button
              href={loginHref}
              variant={ctaVariant}
              size="small"
              className={`${ctaClassName} hidden [html[data-has-session=false]_&]:flex`}
              {...warmupHandlers(warmSignInIntent)}
              onClick={() => posthog.capture("sign_in_clicked", { source: pathname })}
            >
              Sign in
            </Button>
          </>
        )}
        <button
          ref={menuButtonRef}
          type="button"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          aria-controls="site-mobile-menu"
          onClick={onToggleMobile}
          className={`${pillShadowClassName} ${styles.mobileMenuButtonClassName} flex size-8 cursor-pointer items-center justify-center rounded-lg transition-colors focus:outline-2 focus:-outline-offset-2 focus:outline-transparent focus:transition-none focus-visible:outline-focus md:hidden`}
        >
          {mobileOpen ? (
            <CloseIcon size={16} className="text-[#333]" />
          ) : (
            <MenuIcon size={16} className="text-[#333]" />
          )}
        </button>
      </div>
    </>
  )
}

export function SiteHeader({ variant = "standard" }: SiteHeaderProps) {
  const location = useLocation()
  const isNotFound = useRouterState({
    select: (state) =>
      state.matches.some((match) => match.status === "notFound") ||
      (state.matches.length === 1 && state.location.pathname !== "/"),
  })
  const loginHref = isNotFound
    ? `/login?${new URLSearchParams({ next: location.href }).toString()}`
    : "/login"
  const [mobileState, setMobileState] = useState({ pathname: location.pathname, open: false })
  if (mobileState.pathname !== location.pathname) {
    setMobileState({ pathname: location.pathname, open: false })
  }
  const mobileOpen = mobileState.pathname === location.pathname && mobileState.open
  const setMobileOpen = useCallback(
    (nextOpen: boolean | ((open: boolean) => boolean)) => {
      setMobileState((current) => ({
        pathname: location.pathname,
        open: typeof nextOpen === "function" ? nextOpen(current.open) : nextOpen,
      }))
    },
    [location.pathname],
  )
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const wasMobileOpenRef = useRef(false)
  const effectiveVariant = getEffectiveHeaderVariant(variant, mobileOpen)
  const hero = effectiveVariant === "hero"

  // While the fullscreen menu is open, close on Escape, trap focus, lock
  // background scroll, and make the page behind the menu inert.
  useEffect(() => {
    if (!mobileOpen) return
    const openingMenuButton = menuButtonRef.current
    const main = openingMenuButton?.closest("main")
    const headerSection = openingMenuButton?.closest("section")
    const inertElements = main
      ? Array.from(main.children).filter(
          (element) => element.id !== "site-mobile-menu" && element !== headerSection,
        )
      : []

    for (const element of inertElements) {
      setElementInert(element, true)
    }

    const focusFrame = window.requestAnimationFrame(() => {
      const menu = menuRef.current

      if (!menu) {
        return
      }

      menu.focus({ preventScroll: true })
    })

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setMobileOpen(false)
        return
      }

      if (event.key !== "Tab") {
        return
      }

      const menu = menuRef.current
      if (!menu) {
        return
      }

      const focusableElements = getFocusableElements(menu)

      if (focusableElements.length === 0) {
        event.preventDefault()
        menu.focus()
        return
      }

      const firstElement = focusableElements[0]!
      const lastElement = focusableElements[focusableElements.length - 1]!

      if (document.activeElement === menu) {
        event.preventDefault()
        ;(event.shiftKey ? lastElement : firstElement).focus()
      } else if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }
    const desktopQuery = window.matchMedia("(min-width: 48rem)")
    const closeOnDesktop = () => {
      if (desktopQuery.matches) setMobileOpen(false)
    }
    window.addEventListener("keydown", onKeyDown)
    desktopQuery.addEventListener("change", closeOnDesktop)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      window.cancelAnimationFrame(focusFrame)
      window.removeEventListener("keydown", onKeyDown)
      desktopQuery.removeEventListener("change", closeOnDesktop)
      document.body.style.overflow = previousOverflow

      for (const element of inertElements) {
        setElementInert(element, false)
      }
    }
  }, [mobileOpen, setMobileOpen])

  // The hero and standard shells intentionally remount the toggle. Restore
  // focus only after the closing render, when the ref points at the live node.
  useEffect(() => {
    const wasMobileOpen = wasMobileOpenRef.current
    wasMobileOpenRef.current = mobileOpen

    if (wasMobileOpen && !mobileOpen) {
      menuButtonRef.current?.focus()
    }
  }, [mobileOpen])

  const content = (
    <HeaderContent
      loginHref={loginHref}
      variant={effectiveVariant}
      pathname={location.pathname}
      mobileOpen={mobileOpen}
      menuButtonRef={menuButtonRef}
      onToggleMobile={() => setMobileOpen((open) => !open)}
    />
  )

  return (
    <>
      {hero ? (
        <HeroHeaderShell>{content}</HeroHeaderShell>
      ) : (
        <StandardHeaderShell mobileOpen={mobileOpen}>{content}</StandardHeaderShell>
      )}
      {mobileOpen && (
        <SiteMobileMenu items={siteNav} menuRef={menuRef} onNavigate={() => setMobileOpen(false)} />
      )}
    </>
  )
}
